import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PlusCircle, Pencil, Trash2, Send, CheckCircle2, Undo2, Trash, X } from "lucide-react";

const TABS = [
  { key: "all", label: "सभी" },
  { key: "published", label: "प्रकाशित" },
  { key: "pending", label: "समीक्षा में" },
  { key: "draft", label: "ड्राफ्ट" },
];

const statusBadge = (s) => {
  const map = { published: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", draft: "bg-gray-100 text-gray-600" };
  const label = { published: "प्रकाशित", pending: "समीक्षा में", draft: "ड्राफ्ट" };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${map[s] || map.draft}`}>{label[s] || s}</span>;
};

export default function ArticlesList() {
  const { user } = useAuth();
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const isAdmin = user?.role === "admin";
  const canPublish = user?.role === "admin" || user?.role === "editor";

  const load = useCallback(async () => {
    const q = tab === "all" ? "" : `?status=${tab}`;
    const { data } = await api.get(`/articles${q}`);
    setItems(data);
    setSelected(new Set());
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => s.size === items.length ? new Set() : new Set(items.map((x) => x.id)));

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} खबरें हटाएँ? यह वापस नहीं आएंगी।`)) return;
    setBulkBusy(true);
    try {
      await api.post("/articles/bulk-delete", { ids: Array.from(selected) });
      load();
    } catch (e) { alert("कुछ ख़बरें हट नहीं सकीं"); }
    finally { setBulkBusy(false); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/articles/${id}/status`, { status });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("क्या आप इस खबर को हटाना चाहते हैं? यह वापस नहीं आएगी।")) return;
    await api.delete(`/articles/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 font-serif-dev">खबरें</h1>
        <Link to="/admin/articles/new" data-testid="list-new-btn" className="inline-flex items-center gap-2 bg-[#D92525] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0A0F1E] transition-colors">
          <PlusCircle size={16} /> नई खबर
        </Link>
      </div>

      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? "bg-[#0A0F1E] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {isAdmin && selected.size > 0 && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg pl-3 pr-1 py-1" data-testid="bulk-bar">
            <span className="text-sm text-[#D92525] font-semibold">{selected.size} चयनित</span>
            <button onClick={() => setSelected(new Set())} data-testid="bulk-clear"
              className="p-1.5 rounded hover:bg-red-100 text-[#D92525]" title="चयन साफ़"><X size={14} /></button>
            <button onClick={bulkDelete} disabled={bulkBusy} data-testid="bulk-delete"
              className="inline-flex items-center gap-1.5 bg-[#D92525] text-white px-3 py-1.5 rounded text-sm font-semibold hover:bg-[#0A0F1E] disabled:opacity-60">
              <Trash size={13} /> {bulkBusy ? "हटा रहे…" : "हटाएँ"}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
            <tr>
              {isAdmin && (
                <th className="w-10 text-left pl-6 pr-2 py-3">
                  <input
                    type="checkbox"
                    data-testid="bulk-select-all"
                    checked={items.length > 0 && selected.size === items.length}
                    onChange={toggleAll}
                    className="accent-[#D92525] cursor-pointer"
                  />
                </th>
              )}
              <th className={`text-left ${isAdmin ? "pl-2" : "pl-6"} px-4 py-3 font-medium`}>खबर</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">लेखक</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">श्रेणी</th>
              <th className="text-left px-4 py-3 font-medium">स्थिति</th>
              <th className="text-right px-6 py-3 font-medium">कार्रवाई</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.length === 0 && (
              <tr><td colSpan={isAdmin ? 6 : 5} className="px-6 py-10 text-center text-gray-400">कोई खबर नहीं मिली</td></tr>
            )}
            {items.map((a) => (
              <tr key={a.id} data-testid={`article-row-${a.id}`} className={`hover:bg-gray-50 ${selected.has(a.id) ? "bg-red-50/50" : ""}`}>
                {isAdmin && (
                  <td className="pl-6 pr-2 py-3">
                    <input
                      type="checkbox"
                      data-testid={`bulk-select-${a.id}`}
                      checked={selected.has(a.id)}
                      onChange={() => toggle(a.id)}
                      className="accent-[#D92525] cursor-pointer"
                    />
                  </td>
                )}
                <td className={`${isAdmin ? "pl-2" : "pl-6"} px-4 py-3`}>
                  <div className="flex items-center gap-3">
                    <img src={a.thumbnail} alt="" className="w-12 h-9 rounded object-cover bg-gray-100 shrink-0" />
                    <span className="font-serif-dev font-semibold text-gray-900 line-clamp-1 max-w-[320px]">{a.title}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{a.author_name}</td>
                <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{a.category}</td>
                <td className="px-4 py-3">{statusBadge(a.status)}</td>
                <td className="px-6 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <Link to={`/admin/articles/${a.id}/edit`} data-testid={`edit-${a.id}`} title="संपादित करें" className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"><Pencil size={15} /></Link>
                    {a.status !== "published" && canPublish && (
                      <button data-testid={`publish-${a.id}`} onClick={() => setStatus(a.id, "published")} title="प्रकाशित करें" className="p-2 rounded-lg hover:bg-green-50 text-green-600"><CheckCircle2 size={15} /></button>
                    )}
                    {a.status === "draft" && !canPublish && (
                      <button data-testid={`submit-${a.id}`} onClick={() => setStatus(a.id, "pending")} title="समीक्षा हेतु भेजें" className="p-2 rounded-lg hover:bg-amber-50 text-amber-600"><Send size={15} /></button>
                    )}
                    {a.status === "published" && canPublish && (
                      <button data-testid={`unpublish-${a.id}`} onClick={() => setStatus(a.id, "draft")} title="अप्रकाशित करें" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><Undo2 size={15} /></button>
                    )}
                    {isAdmin && (
                      <button data-testid={`delete-${a.id}`} onClick={() => remove(a.id)} title="हटाएँ" className="p-2 rounded-lg hover:bg-red-50 text-[#D92525]"><Trash2 size={15} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!isAdmin && (
        <p className="text-xs text-gray-400 mt-3">नोट: खबर हटाने (delete) का अधिकार केवल एडमिन को है।</p>
      )}
    </div>
  );
}
